/**
 * The cookie that lets this browser fetch aircraft HLS (`/hls/<sn>.m3u8` and its segments).
 *
 * Host nginx checks every `/hls/` request with `auth_request` against the backend
 * (`main.hls_auth`), which reads an `ow_hls` cookie scoped to `Path=/hls/`. A cookie rather
 * than a header because Safari/iOS play HLS natively and cannot add one, and because a
 * playlist lists its segments by relative path, so a token in the playlist URL would not
 * reach them. One cookie serves every tile, so this module is shared state: it fetches once,
 * renews before expiry while any player holds it, and refetches at once after a refusal.
 *
 * Every failure resolves `false` and the player tries anyway: on a deployment whose nginx
 * does not check yet, or in local dev with no nginx at all, the stream plays regardless.
 */
export type HeadersFn = (contentType?: boolean) => HeadersInit;

let validUntil = 0;
let inflight: Promise<boolean> | null = null;
let renewTimer: number | null = null;
let holders = 0;

async function fetchCookie(apiUrl: string, headers: HeadersFn): Promise<boolean> {
  try {
    const res = await fetch(`${apiUrl}/api/drone/hls-cookie`, {
      method: 'POST',
      headers: headers(),
      credentials: 'include',
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { expires_in?: number; renew_in_s?: number };
    const ttl = body.expires_in ?? 600;
    validUntil = Date.now() + ttl * 1000;
    if (renewTimer !== null) window.clearTimeout(renewTimer);
    const renewIn = (body.renew_in_s ?? Math.floor(ttl / 3)) * 1000;
    renewTimer = window.setTimeout(() => {
      renewTimer = null;
      if (holders > 0) void ensureHlsCookie(apiUrl, headers, true);
    }, renewIn);
    return true;
  } catch {
    return false;
  }
}

/** Resolve once this browser holds a fresh cookie. `force` refetches (after a refusal). */
export function ensureHlsCookie(apiUrl: string, headers: HeadersFn, force = false): Promise<boolean> {
  if (!force && Date.now() < validUntil - 30_000) return Promise.resolve(true);
  if (!inflight) {
    inflight = fetchCookie(apiUrl, headers).finally(() => { inflight = null; });
  }
  return inflight;
}

/** A player is using the cookie: keep renewing it until every holder releases. */
export function holdHlsCookie(): () => void {
  holders += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders = Math.max(0, holders - 1);
  };
}

/** Test seam: forget everything. */
export function resetHlsCookieForTests(): void {
  validUntil = 0;
  inflight = null;
  holders = 0;
  if (renewTimer !== null) window.clearTimeout(renewTimer);
  renewTimer = null;
}
